/* ==========================================================================
   Custom — JS global (kit pipeline)
   Chargé via snippets/custom-globals.liquid. Script classique (pas de module),
   volontairement indépendant de l'importmap d'Horizon pour survivre aux updates.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Vue 2e image (« Vue en situation ») — pages collection
   Bascule toutes les cartes produit sur leur 2e image. La 2e image est déjà
   présente dans le DOM grâce au réglage natif « show_second_image_on_hover »
   (voir snippets/card-gallery.liquid), donc aucune requête supplémentaire.
   Le choix est mémorisé dans localStorage. En vue mannequin, l'effet de survol
   est neutralisé : l'image reste figée sur la photo portée.
   -------------------------------------------------------------------------- */
(() => {
  'use strict';

  const STORAGE_KEY = 'custom:model-view';
  const BUTTON_CLASS = 'custom-model-view';
  const WRAPPER_SELECTOR = '.facets-controls-wrapper';

  const readPref = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const writePref = (on) => {
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch (error) {
      /* navigation privée ou stockage bloqué : le toggle marche quand même, sans mémoire */
    }
  };

  let modelView = readPref();

  /**
   * Bascule une carte produit sur sa 2e image, ou la remet sur la première.
   * Les produits à une seule image sont ignorés : ils gardent leur photo.
   */
  const applyToCard = (card) => {
    const gallery = card.querySelector('[ref="cardGallery"]');
    const slideshow = card.querySelector('slideshow-component');

    if (!gallery || !slideshow || typeof slideshow.select !== 'function') return;

    const slides = slideshow.slides;
    if (!slides || slides.length < 2) return;

    if (modelView) {
      // Fige l'image : on retire les hooks de survol d'Horizon en mémorisant leur valeur.
      if (gallery.hasAttribute('on:pointerenter')) {
        gallery.dataset.customHoverEnter = gallery.getAttribute('on:pointerenter');
        gallery.dataset.customHoverLeave = gallery.getAttribute('on:pointerleave') || '';
        gallery.removeAttribute('on:pointerenter');
        gallery.removeAttribute('on:pointerleave');
      }

      if (slideshow.current !== 1) slideshow.select(1, undefined, { animate: false });
    } else {
      if ('customHoverEnter' in gallery.dataset) {
        gallery.setAttribute('on:pointerenter', gallery.dataset.customHoverEnter);
        if (gallery.dataset.customHoverLeave) {
          gallery.setAttribute('on:pointerleave', gallery.dataset.customHoverLeave);
        }
        delete gallery.dataset.customHoverEnter;
        delete gallery.dataset.customHoverLeave;
      }

      if (slideshow.current !== 0) slideshow.select(0, undefined, { animate: false });
    }
  };

  const applyAll = () => {
    document.querySelectorAll('product-card').forEach(applyToCard);
  };

  const syncButtons = () => {
    document.querySelectorAll('.' + BUTTON_CLASS).forEach((button) => {
      button.setAttribute('aria-checked', modelView ? 'true' : 'false');
    });
  };

  /*
    Un seul écouteur délégué pour tous les toggles : les copies posées dans les
    barres sont recréées à chaque re-rendu de la section, un écouteur par bouton
    serait perdu avec elles.
  */
  const onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.' + BUTTON_CLASS)) return;

    modelView = !modelView;
    writePref(modelView);
    syncButtons();
    applyAll();
  };

  /** Repli : markup identique au bloc blocks/custom-model-view-toggle.liquid. */
  const buildToggle = () => {
    const button = document.createElement('button');

    button.type = 'button';
    // Contrôle segmenté (2026-10-28) : « Fond uni » | « En situation ». Même bouton
    // role=switch qu'avant (aria-checked = vue en situation), seul le rendu change.
    button.className = BUTTON_CLASS + ' ' + BUTTON_CLASS + '--seg';
    button.setAttribute('role', 'switch');
    button.setAttribute('aria-label', 'Vue en situation');
    button.innerHTML =
      '<span class="custom-model-view__seg custom-model-view__seg--off" aria-hidden="true">Fond uni</span>' +
      '<span class="custom-model-view__seg custom-model-view__seg--on" aria-hidden="true">En situation</span>';

    return button;
  };

  /*
    Le toggle n'existe que sur les pages collection, dans l'en-tête des filtres.
    Horizon rend deux barres (.facets-controls-wrapper) : celle de l'ordinateur et
    celle du mobile, affichées tour à tour en CSS. Chacune reçoit son toggle.
    Si Victor a posé le bloc « Toggle vue 2e image » sur la page, chaque toggle
    est une copie de CE bouton (il porte ses réglages de design) ; l'original reste
    à sa place, masqué par custom.css. Sinon on injecte un toggle par défaut.
  */
  const injectToggle = () => {
    const collection = document.querySelector('collection-component');
    if (!collection) return;

    const source = document.querySelector('.' + BUTTON_CLASS + ':not(' + WRAPPER_SELECTOR + ' *)');

    collection.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      if (wrapper.querySelector('.' + BUTTON_CLASS)) return;
      wrapper.appendChild(source ? source.cloneNode(true) : buildToggle());
    });

    syncButtons();
  };

  /*
    La grille est re-rendue par la Section Rendering API (filtres, tri, scroll infini).
    On réapplique après chaque morph. Les gardes `slideshow.current !== x` et
    `wrapper.querySelector('.' + BUTTON_CLASS)` rendent l'opération idempotente, donc
    l'observer converge au lieu de boucler sur ses propres mutations.
  */
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      injectToggle();
      applyAll();
    });
  };

  const watchResults = () => {
    const list = document.querySelector('results-list');
    if (!list) return;

    new MutationObserver(schedule).observe(list, { childList: true, subtree: true });
  };

  const init = async () => {
    if (!document.querySelector('collection-component')) return;

    // Attend que les composants Horizon soient définis, sinon slideshow.select n'existe pas encore.
    await Promise.all([
      customElements.whenDefined('slideshow-component'),
      customElements.whenDefined('product-card'),
    ]);

    injectToggle();
    applyAll();
    watchResults();

    document.addEventListener('click', onClick);

    // Éditeur de thème : un réglage du bloc a changé, on repart du bouton fraîchement rendu.
    document.addEventListener('shopify:section:load', () => {
      document.querySelectorAll(WRAPPER_SELECTOR + ' .' + BUTTON_CLASS).forEach((button) => button.remove());
      schedule();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --------------------------------------------------------------------------
   Recommandations produit (custom) — blocks/custom-product-recommendations.liquid
   Met à jour le prix affiché quand on change de variante dans une carte.
   L'ajout au panier n'est pas géré ici : le <select> porte name="id" et le
   <product-form-component> natif d'Horizon fait le reste.
   Écoute déléguée sur document : les cartes arrivent après coup (chargement
   asynchrone des recommandations) et sont re-rendues par morph.
   -------------------------------------------------------------------------- */
(() => {
  'use strict';

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-custom-reco-select]')) return;

    const card = select.closest('.custom-reco__card');
    const option = select.selectedOptions[0];
    if (!card || !option) return;

    const price = card.querySelector('[data-custom-reco-price]');
    const compare = card.querySelector('[data-custom-reco-compare]');

    if (price) price.innerHTML = option.dataset.price || '';
    if (compare) {
      compare.innerHTML = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
  });
})();

/* --------------------------------------------------------------------------
   Livraison estimée + offre quantité (blocs custom, 2026-10-18)
   1. Livraison estimée : blocks/custom-delivery-estimate.liquid
      « Livrée entre le … et le … » en jours ouvrés (lundi-vendredi).
   2. Offre quantité : blocks/custom-bundle-offer.liquid
      Le choix 1 / 2 / 3 règle le champ quantité du formulaire produit
      (input[name="quantity"] de la même section). Aucune remise appliquée ici :
      ce sont les réductions automatiques Shopify qui s'en chargent au panier.
   -------------------------------------------------------------------------- */
(() => {
  const addBusinessDays = (date, days) => {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  };

  const formatDay = (date) =>
    date
      .toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

  const initDeliveryEstimates = () => {
    document.querySelectorAll('[data-custom-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return;
      const today = new Date();
      const from = addBusinessDays(today, min);
      const to = addBusinessDays(today, max);
      el.textContent = `${el.dataset.prefix} ${formatDay(from)} ${el.dataset.between} ${formatDay(to)}`;
    });
  };

  const setQuantity = (fieldset, qty) => {
    const scope = fieldset.closest('.shopify-section') || document;
    const inputs = scope.querySelectorAll('input[name="quantity"]');
    inputs.forEach((input) => {
      input.value = String(qty);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const onBundleChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;
    const fieldset = radio.closest('[data-custom-bundle]');
    if (!fieldset) return;
    setQuantity(fieldset, parseInt(radio.value, 10) || 1);
  };

  const init = () => {
    initDeliveryEstimates();
    document.addEventListener('change', onBundleChange);
  };

  // Le bloc est re-rendu quand on change de variante (morph Horizon) : on recalcule les dates.
  document.addEventListener('shopify:section:load', initDeliveryEstimates);
  new MutationObserver(() => {
    document.querySelectorAll('[data-custom-delivery]').forEach((el) => {
      if (!el.dataset.done || el.textContent.indexOf(el.dataset.prefix) !== 0) {
        el.dataset.done = '1';
        initDeliveryEstimates();
      }
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/*
 * Fiche technique courte (blocks/custom-product-specs.liquid), 2026-10-24.
 * Met à jour les dimensions de la variante choisie. Écoute l'événement standard
 * Shopify « shopify:product:select » émis par le sélecteur de variantes natif
 * (pas de monkey-patch du web component), puis lit la table JSON variante -> dimensions
 * posée dans le bloc. Les sélecteurs de cartes produit (quick add) sont ignorés.
 */
(() => {
  const variantNumber = (value) => (value == null ? '' : String(value).split('/').pop());

  const updateSpecs = (scope, variantId) => {
    const id = variantNumber(variantId);
    if (!id) return;
    scope.querySelectorAll('[data-custom-specs]').forEach((block) => {
      if (block.closest('product-card')) return;
      const mapEl = block.querySelector('[data-custom-dims-map]');
      const item = block.querySelector('[data-custom-dims-item]');
      const out = block.querySelector('[data-custom-dims]');
      if (!mapEl || !item || !out) return;
      let map;
      try {
        map = JSON.parse(mapEl.textContent || '{}');
      } catch (error) {
        return;
      }
      if (!(id in map)) return;
      const value = map[id];
      out.textContent = value || '';
      item.hidden = !value;
    });
  };

  document.addEventListener('shopify:product:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;
    const scope = target.closest('.shopify-section, dialog') || document;
    updateSpecs(scope, event.detail && event.detail.variantId);
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise
        .then((result) => {
          if (result && result.variant) updateSpecs(scope, result.variant.id);
        })
        .catch(() => {});
    }
  });
})();

/*
 * Recharge pour housse vide (blocks/custom-product-recharge.liquid), 2026-10-24.
 * Recalcule le volume conseillé, le nombre de sacs et le total à chaque changement de
 * variante. Même mécanique que la fiche courte : événement standard shopify:product:select,
 * table JSON variante -> litres posée dans le bloc. Cartes produit (quick add) ignorées.
 */
(() => {
  const variantNumber = (value) => (value == null ? '' : String(value).split('/').pop());

  const formatMoney = (cents, block) => {
    try {
      return new Intl.NumberFormat(block.dataset.locale || 'fr', {
        style: 'currency',
        currency: block.dataset.currency || 'EUR',
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  };

  const updateRecharge = (scope, variantId) => {
    const id = variantNumber(variantId);
    if (!id) return;
    scope.querySelectorAll('[data-custom-recharge]').forEach((block) => {
      if (block.closest('product-card')) return;
      const mapEl = block.querySelector('[data-recharge-map]');
      if (!mapEl) return;
      let map;
      try {
        map = JSON.parse(mapEl.textContent || '{}');
      } catch (error) {
        return;
      }
      if (!(id in map)) return;
      const litres = Number(map[id]) || 0;
      if (!litres) {
        block.hidden = true;
        return;
      }
      const perBag = Number(block.dataset.perBag) || 22;
      const unit = Number(block.dataset.unitPrice) || 0;
      const bags = Math.ceil(litres / perBag);
      const set = (selector, value) => {
        const el = block.querySelector(selector);
        if (el) el.textContent = value;
      };
      set('[data-recharge-litres]', String(litres));
      set('[data-recharge-bags]', String(bags));
      set('[data-recharge-total]', formatMoney(unit * bags, block));
      const qty = block.querySelector('[data-recharge-qty]');
      if (qty) qty.value = String(bags);
      block.hidden = false;
    });
  };

  document.addEventListener('shopify:product:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;
    const scope = target.closest('.shopify-section, dialog') || document;
    updateRecharge(scope, event.detail && event.detail.variantId);
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise
        .then((result) => {
          if (result && result.variant) updateRecharge(scope, result.variant.id);
        })
        .catch(() => {});
    }
  });
})();

/* --------------------------------------------------------------------------
   Barre d'ajout collante : nom court du produit en surtitre (2026-10-28).
   Lit le nom court affiché par blocks/product-title.liquid (.custom-ptitle__name)
   et l'expose au CSS via --custom-sticky-eyebrow + [data-custom-eyebrow]
   (voir custom.css, section 13). Sans nom court : rien ne change.
   -------------------------------------------------------------------------- */
(() => {
  const initStickyEyebrow = () => {
    const name = document.querySelector('.custom-ptitle__name');
    const sticky = document.querySelector('sticky-add-to-cart');
    if (!name || !sticky) return;
    const text = name.textContent.trim();
    if (!text) return;
    sticky.style.setProperty('--custom-sticky-eyebrow', JSON.stringify(text));
    sticky.setAttribute('data-custom-eyebrow', '');
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyEyebrow);
  } else {
    initStickyEyebrow();
  }
  document.addEventListener('shopify:section:load', initStickyEyebrow);
})();

/* --------------------------------------------------------------------------
   Galerie produit : barre « compteur + flèches » sous l'image (2026-10-28).
   Pose, entre l'image et les miniatures du carrousel natif (media-gallery
   en carousel + miniatures), une barre avec « 1/N » à gauche et les flèches
   précédent / suivant à droite (voir custom.css, section 17). Pilote le
   slideshow-component natif via son API publique (next, previous, current) et
   l'événement slideshow:select. Re-posée quand Horizon remplace la galerie
   (changement de variante) ou recharge la section. Sans miniatures (une seule
   image) ou sans carrousel : rien ne change.
   Cible : media-gallery.media-gallery--carousel, slideshow-component,
   slideshow-container, slideshow-controls[thumbnails], .slideshow-controls__thumbnail,
   slideshow-arrows .slideshow-control--previous / --next (libellés d'accessibilité)
   -------------------------------------------------------------------------- */
(() => {
  const ICONS = {
    previous: 'M20 12H4M10 6l-6 6 6 6',
    next: 'M4 12h16M14 6l6 6-6 6',
  };

  const makeButton = (action, label, onClick) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'custom-gallery-bar__btn custom-gallery-bar__btn--' + action;
    button.setAttribute('aria-label', label);
    button.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="' +
      ICONS[action] +
      '"/></svg>';
    button.addEventListener('click', onClick);
    return button;
  };

  const buildBar = (gallery) => {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || slideshow.querySelector(':scope > .custom-gallery-bar')) return;
    const container = slideshow.querySelector(':scope > slideshow-container');
    const controls = slideshow.querySelector(':scope > slideshow-controls[thumbnails]');
    if (!container || !controls) return;
    const total = controls.querySelectorAll('.slideshow-controls__thumbnail').length;
    if (total < 2) return;

    const labelOf = (action, fallback) => {
      const native = slideshow.querySelector('slideshow-arrows .slideshow-control--' + action);
      return (native && native.getAttribute('aria-label')) || fallback;
    };

    const bar = document.createElement('div');
    bar.className = 'custom-gallery-bar';

    const count = document.createElement('div');
    count.className = 'custom-gallery-bar__count';
    count.setAttribute('aria-live', 'polite');
    const current = document.createElement('span');
    current.className = 'custom-gallery-bar__current';
    count.append(current, '/' + total);

    const arrows = document.createElement('div');
    arrows.className = 'custom-gallery-bar__arrows';
    arrows.append(
      makeButton('previous', labelOf('previous', 'Précédent'), (event) => {
        if (typeof slideshow.previous === 'function') slideshow.previous(event);
      }),
      makeButton('next', labelOf('next', 'Suivant'), (event) => {
        if (typeof slideshow.next === 'function') slideshow.next(event);
      })
    );

    bar.append(count, arrows);
    container.after(bar);

    const sync = () => {
      const index = Number(slideshow.current);
      current.textContent = String((Number.isFinite(index) ? index : 0) + 1);
    };
    sync();
    slideshow.addEventListener('slideshow:select', sync);
  };

  const initGalleryBar = () => {
    document.querySelectorAll('media-gallery.media-gallery--carousel').forEach(buildBar);
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      initGalleryBar();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleryBar);
  } else {
    initGalleryBar();
  }
  document.addEventListener('shopify:section:load', initGalleryBar);
  // Horizon remplace tout le <media-gallery> au changement de variante : on repose la barre.
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
})();
